import { useTranslates } from "providers/translates";
import { IconLinks } from "../IconLinks";
import { useRedirectAndScroll } from "hooks";
import { useAuth } from "providers/auth-provider";
import styles from './styles.module.scss';
import { isVac } from "utilities/is-vac";
import { Helmet } from 'react-helmet-async';
import '../../header.scss';
import { Language } from "providers/translates/enums";
import { getVacLanguageSwitchUrl } from "utilities/vac-language";
import { vacHeaderLabel } from "utilities/vac-copy";

type Props = {
  headerItemsController: {
    language: boolean;
    howToUse: boolean;
    support: boolean;
    myAccount: boolean;
    donate: boolean;
    aboutUs: boolean;
    contactUs: boolean;
    icons: boolean;
  },
  actionLogout: () => void;
}

const translationsHelmet = {
  descriptionEN: 'Sign in to your Saluting Service account to create or edit a tribute.',
  descriptionFR: 'Connectez-vous à votre compte pour créer ou modifier un hommage.',
  titleEN: 'Saluting Service | Sign in ',
  titleFR: 'Saluons leur service | Connexion '
} as const;


const DesktopMenu: React.FC<Props> = ({
  headerItemsController,
  actionLogout
}) => {
  const { translates: t, selectedLang, setSelectedLang } = useTranslates();
  const isFrench = selectedLang === Language.FRENCH;
  const { onlyRedirect } = useRedirectAndScroll({});
  const { isAuthorized, user } = useAuth();

  const handleChangeLanguage = () => {
    const newLanguage = selectedLang === Language.ENGLISH ? Language.FRENCH : Language.ENGLISH;
    setSelectedLang(newLanguage);

    const switchUrl = getVacLanguageSwitchUrl(newLanguage);
    if (switchUrl) {
      window.location.assign(switchUrl);
    }
  }

  return (
    <>
    {isVac() && (
        <Helmet>
          <meta name="description" content={isFrench ? translationsHelmet?.descriptionFR : translationsHelmet?.descriptionEN} />
          <title>{isFrench ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
        </Helmet>
      )}
    <div className={styles['header__info']}>
      {(headerItemsController.language && isVac()) && (
        <div className={"header__lang"}>
          <span
            className="-active"
            onClick={handleChangeLanguage}
          >
            {isFrench ? 'English' : 'Français'}
          </span>
        </div>
      )}

      {headerItemsController.aboutUs && (
        <div 
          className="header__link hidden" 
          onClick={() => onlyRedirect('/about')}
        >
          <span>{vacHeaderLabel(selectedLang, t?.header?.about, 'about', 'About')}</span>
        </div>
      )}

      {headerItemsController.contactUs && (
        <a
          className="header__link hidden"
          target={'_blank'}
          rel="noopener noreferrer"
          href="mailto:info@virtualhospice.ca?subject=I have questions about the MemorialQuilt"
        >
          <span>{vacHeaderLabel(selectedLang, t?.header?.contact, 'contact', 'Contact Us')}</span>
        </a>
      )}

      {headerItemsController.howToUse && (
        <div 
          className="header__link hidden" 
          onClick={() => onlyRedirect('/howtouse')}
        >
          <span>{vacHeaderLabel(selectedLang, t?.header?.howToUse, 'howToUse', 'How To Use')}</span>
        </div>
      )}

      {headerItemsController.support && (
        <div 
          className="header__link hidden" 
          onClick={() => onlyRedirect('/resources')}
        >
          {vacHeaderLabel(selectedLang, t?.header?.resources, 'resources', 'Resources')}
        </div>
      )}

      {isAuthorized && headerItemsController.myAccount && (
        <div 
          className="header__link hidden" 
          onClick={() => onlyRedirect('/profile')}
        >
          <span>{vacHeaderLabel(selectedLang, t?.header?.myAccount, 'myAccount', 'My Account')}</span>
        </div>
      )}

      {isAuthorized && !user?.isInvitedUser ? (
        <div 
          className="header__link hidden" 
          onClick={actionLogout}
        >
          <span>{vacHeaderLabel(selectedLang, t?.header?.logout, 'logout', 'Logout')}</span>
        </div>
      ) : (
        <div 
          className="header__link hidden" 
          onClick={() => onlyRedirect('/login')}
        >
          <span>{vacHeaderLabel(selectedLang, t?.header?.signIn, 'signIn', 'Sign In')}</span>
        </div>
      )}

      {headerItemsController.icons && (
        <IconLinks />
      )}
    </div>
  </>
  );
}

export { DesktopMenu };
